import jsPDF from 'jspdf';
import { getEnrichedCriteria } from '../data/scorecardCriteria';

// ─── DESIGN TOKENS ───────────────────────────────────────────────────────────
const TEAL       = '#1A7A6E';
const NEAR_BLACK = '#1A1A1A';
const MID_GREY   = '#555555';
const LIGHT_GREY = '#F5F5F5';
const WHITE      = '#FFFFFF';

const PAGE_W   = 210;
const PAGE_H   = 297;
const MARGIN_L = 22;
const MARGIN_R = 22;
const MARGIN_T = 20;
// eslint-disable-next-line no-unused-vars
const MARGIN_B = 20;
const CONTENT_W = PAGE_W - MARGIN_L - MARGIN_R;

// ─── COLOUR HELPERS ──────────────────────────────────────────────────────────
const hex2rgb = (hex) => [
  parseInt(hex.slice(1,3),16),
  parseInt(hex.slice(3,5),16),
  parseInt(hex.slice(5,7),16),
];
const setFill  = (doc, hex) => { const [r,g,b] = hex2rgb(hex); doc.setFillColor(r,g,b); };
const setDraw  = (doc, hex) => { const [r,g,b] = hex2rgb(hex); doc.setDrawColor(r,g,b); };
const setTextC = (doc, hex) => { const [r,g,b] = hex2rgb(hex); doc.setTextColor(r,g,b); };
const setFont  = (doc, weight='normal', size=10) => { doc.setFont('helvetica', weight); doc.setFontSize(size); };
const wrapText = (doc, text, maxWidth) => doc.splitTextToSize(text || '', maxWidth);

// ─── DRAWN CHECKMARK (Fix 3: replaces broken ✓ glyph) ───────────────────────
const drawCheckmark = (doc, x, y, size=6, color=TEAL) => {
  const prev = doc.getLineWidth();
  setDraw(doc, color);
  doc.setLineWidth(size * 0.15);
  doc.line(x, y, x + size * 0.35, y + size * 0.3);
  doc.line(x + size * 0.35, y + size * 0.3, x + size, y - size * 0.4);
  doc.setLineWidth(prev);
};

// ─── FOOTER — Fix 1 + Fix 5: quiet single-line footer, rendered in two-pass ─
// Called by the two-pass loop at the end of generateHiringKitPDF.
// NOT called from individual section functions.
const renderFooter = (doc, currentPage, totalPages) => {
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const fy = ph - 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(140, 140, 140);
  doc.text('RoleReady · roleready.co.nz', MARGIN_L, fy);
  const pageText = `Page ${currentPage} of ${totalPages}`;
  doc.text(pageText, pw - MARGIN_R, fy, { align: 'right' });
};

// ─── RUNNING HEADER (page 2+): wordmark + role name, no credential ───────────
const addRunningHeader = (doc, role) => {
  setFill(doc, WHITE);
  doc.rect(0, 0, PAGE_W, 14, 'F');
  setFont(doc, 'bold', 9);
  setTextC(doc, TEAL);
  doc.text('RoleReady', MARGIN_L, 9);
    setFont(doc, 'normal', 9);
  setTextC(doc, MID_GREY);
  // Truncate long role names so the header never crowds the wordmark.
  // Reserve space after the "RoleReady" wordmark (ends ~x=42) plus a gap.
  const headerMaxW = (PAGE_W - MARGIN_R) - (MARGIN_L + 24);
  let headerRole = role || '';
  if (doc.getTextWidth(headerRole) > headerMaxW) {
    while (headerRole.length > 4 && doc.getTextWidth(headerRole + '…') > headerMaxW) {
      headerRole = headerRole.slice(0, -1);
    }
    headerRole = headerRole.replace(/[\s/–-]+$/, '') + '…';
  }
  doc.text(headerRole, PAGE_W - MARGIN_R, 9, { align: 'right' });
  setDraw(doc, '#E0E0E0');
  doc.setLineWidth(0.3);
  doc.line(MARGIN_L, 12, PAGE_W - MARGIN_R, 12);
};

// ─── SECTION HEADER ──────────────────────────────────────────────────────────
const addSectionHeader = (doc, number, title, subtitle) => {
  let y = MARGIN_T + 14;
  // Large display numeral: teal, own line
  setFont(doc, 'bold', 28);
  setTextC(doc, TEAL);
  doc.text(String(number).padStart(2, '0'), MARGIN_L, y);
  y += 10;
  // Section title immediately below: 14pt bold black
  setFont(doc, 'bold', 14);
  setTextC(doc, NEAR_BLACK);
  doc.text(title, MARGIN_L, y);
  y += 6;
  if (subtitle) {
    setFont(doc, 'normal', 10);
    setTextC(doc, MID_GREY);
    doc.text(subtitle, MARGIN_L, y);
    y += 6;
  }
  setDraw(doc, TEAL);
  doc.setLineWidth(0.5);
  doc.line(MARGIN_L, y, PAGE_W - MARGIN_R, y);
  y += 8;
  return y;
};

// ─── COVER PAGE ──────────────────────────────────────────────────────────────
// Fix 2: businessName omitted entirely when blank — no "Your Business" fallback
const buildCoverPage = (doc, role, location, businessName, generatedDate) => {
  setFill(doc, TEAL);
  doc.rect(0, 0, PAGE_W, 80, 'F');

  // Wordmark: small, muted, top-left corner
  setFont(doc, 'normal', 8);
  setTextC(doc, '#CCEEEA');
  doc.text('RoleReady', MARGIN_L, 15);

    // Role name: hero — largest text on the page.
  // Shrink-to-fit then wrap so long titles never clip off the teal block.
  const coverTitle = role || 'Hiring Kit';
  let coverTitleSize = 28;
  setFont(doc, 'bold', coverTitleSize);
  while (doc.getTextWidth(coverTitle) > CONTENT_W && coverTitleSize > 18) {
    coverTitleSize -= 1;
    setFont(doc, 'bold', coverTitleSize);
  }
  setTextC(doc, WHITE);
  const coverTitleLines = wrapText(doc, coverTitle, CONTENT_W);
  // Anchor baseline so 1 line sits at y=42; extra lines stack upward slightly
  const coverTitleY = 42 - (coverTitleLines.length - 1) * (coverTitleSize * 0.42);
  doc.text(coverTitleLines, MARGIN_L, coverTitleY);

  // Region + date: 11pt muted, 4pt gap below role name
  setFont(doc, 'normal', 11);
  setTextC(doc, '#CCEEEA');
  doc.text(`${location || 'New Zealand'} · ${generatedDate}`, MARGIN_L, 50);


  let y = 98;

  // Fix 2: Only render "Prepared for" when a business name was entered
  if (businessName && businessName.trim()) {
    setFont(doc, 'bold', 9);
    setTextC(doc, TEAL);
    doc.text('PREPARED FOR', MARGIN_L, y);
    y += 5;
    setFont(doc, 'bold', 14);
    setTextC(doc, NEAR_BLACK);
    doc.text(businessName.trim(), MARGIN_L, y);
    y += 8;
  } else {
    // No business name — add a little extra whitespace to keep cover balanced
    y += 6;
  }

  setFont(doc, 'normal', 10);
  setTextC(doc, MID_GREY);
  doc.text(`Role: ${role || ''}`, MARGIN_L, y); y += 5;
  doc.text(`Region: ${location || 'New Zealand'}`, MARGIN_L, y); y += 5;
  doc.text(`Generated: ${generatedDate}`, MARGIN_L, y); y += 14;

  setFont(doc, 'bold', 11);
  setTextC(doc, NEAR_BLACK);
  doc.text("What's inside", MARGIN_L, y);
  y += 7;

  [
    'Section 01 — Job Advertisement  (platform-formatted, ready to post)',
    'Section 02 — NZ Pay Benchmark  (a pay reference point for your role and region)',
    'Section 03 — Applicant Screening Grid  (sift CVs against fixed criteria before you interview)',
    'Section 04 — Interview Pack  (questions with coaching notes)',
    'Section 05 — Candidate Scorecard  (role-calibrated rating criteria)',
  ].forEach(s => {
    setFont(doc, 'normal', 10);
    setTextC(doc, MID_GREY);
    const lines = wrapText(doc, s, CONTENT_W - 6);
    doc.text('·', MARGIN_L, y);
    doc.text(lines, MARGIN_L + 5, y);
    y += lines.length * 5.5 + 1;
  });

  y += 14;
  setFill(doc, LIGHT_GREY);
  doc.rect(MARGIN_L, y, CONTENT_W, 44, 'F');
  setFont(doc, 'bold', 9);
  setTextC(doc, NEAR_BLACK);
  doc.text('How to use this kit', MARGIN_L + 5, y + 7);
  setFont(doc, 'normal', 9);
  setTextC(doc, MID_GREY);
  [
    '1. Post your job ad from Section 01 directly to your chosen platform.',
    '2. Use the pay benchmark (Section 02) as a reference when you set your offer.',
    '3. Set your screening criteria (Section 03), then sift every CV against them.',
    '4. Follow the interview pack (Section 04) question by question.',
    '5. Use the scorecard (Section 05) to rate every candidate consistently.',
  ].forEach((line, i) => {
    doc.text(line, MARGIN_L + 5, y + 14 + (i * 5.5));
  });
};

// ─── SECTION 01 — JOB AD ─────────────────────────────────────────────────────
const buildJobAdSection = (doc, role, location, jobAdData, platform, pageTracker) => {
  doc.addPage();
  pageTracker.current++;
  addRunningHeader(doc, role);

  let y = addSectionHeader(doc, 1, 'Job Advertisement', 'Platform-formatted, ready to post');

  const seekAd    = jobAdData?.seekAd;
  const tradeMeAd = jobAdData?.tradeMeAd;

  const renderField = (label, value, startY) => {
    if (!value) return startY;
    let cy = startY;
    const textLines = wrapText(doc, value, CONTENT_W);
    const blockH = 6 + (textLines.length * 4.5) + 4;
    if (cy + blockH > PAGE_H - 20) {
      doc.addPage();
      pageTracker.current++;
      addRunningHeader(doc, role);
      cy = MARGIN_T + 16;
    }
    setFont(doc, 'bold', 7);
    setTextC(doc, MID_GREY);
    doc.text(label, MARGIN_L, cy);
    cy += 4;
    setFont(doc, 'normal', 10);
    setTextC(doc, NEAR_BLACK);
    doc.text(textLines, MARGIN_L, cy);
    return cy + (textLines.length * 4.5) + 5;
  };

  const renderSeekAd = (ad, startY) => {
    let cy = startY;
    setFont(doc, 'bold', 11);
    setTextC(doc, TEAL);
    doc.text('SEEK NZ', MARGIN_L, cy);
    cy += 6;
    setDraw(doc, '#E0E0E0');
    doc.setLineWidth(0.2);
    doc.line(MARGIN_L, cy, PAGE_W - MARGIN_R, cy);
    cy += 6;

    cy = renderField('JOB TITLE', ad.jobTitle, cy);
    if (ad.highlights && ad.highlights.length > 0) {
      const hlText = ad.highlights.map(h => `  ${h}`).join('\n');
      cy = renderField('SEARCH RESULT HIGHLIGHTS', hlText, cy);
    }
    cy = renderField('ABOUT THE ROLE', ad.aboutTheRole, cy);
    cy = renderField("WHAT YOU'LL DO", (ad.whatYoullDo || []).map(b => `  ${b}`).join('\n'), cy);
    cy = renderField("WHAT WE'RE LOOKING FOR", (ad.whatWereLookingFor || []).map(b => `  ${b}`).join('\n'), cy);
    if (ad.whatWeOffer && ad.whatWeOffer.length > 0) {
      cy = renderField('WHAT WE OFFER', (ad.whatWeOffer || []).map(b => `  ${b}`).join('\n'), cy);
    }
    cy = renderField('ABOUT US', ad.aboutUs, cy);
    cy = renderField('APPLICATION', ad.applicationTrigger, cy);
    return cy;
  };

  const renderTradeMeAd = (ad, startY) => {
    let cy = startY;
    setFont(doc, 'bold', 11);
    setTextC(doc, TEAL);
    doc.text('Trade Me Jobs', MARGIN_L, cy);
    cy += 6;
    setDraw(doc, '#E0E0E0');
    doc.setLineWidth(0.2);
    doc.line(MARGIN_L, cy, PAGE_W - MARGIN_R, cy);
    cy += 6;

    cy = renderField('JOB TITLE', ad.jobTitle, cy);

    setFont(doc, 'normal', 7);
    setTextC(doc, '#777777');
    doc.text('Trade Me shows the first 2 lines in search results.', MARGIN_L, cy);
    cy += 5;

    setFont(doc, 'bold', 7);
    setTextC(doc, MID_GREY);
    doc.text('JOB DESCRIPTION', MARGIN_L, cy);
    cy += 4;
    setFont(doc, 'normal', 10);
    const descLines = wrapText(doc, ad.jobDescription, CONTENT_W); // FIX: moved after setFont(10) — Sprint 17 Trade Me truncation
    setTextC(doc, NEAR_BLACK);
    descLines.forEach(line => {
      if (cy + 5 > PAGE_H - 20) {
        doc.addPage();
        pageTracker.current++;
        addRunningHeader(doc, role);
        cy = MARGIN_T + 16;
      }
      doc.text(line, MARGIN_L, cy);
      cy += 4.5;
    });
    return cy;
  };

  if (seekAd && (platform === 'seek' || platform === 'notsure' || platform === 'both')) {
    y = renderSeekAd(seekAd, y);
    y += 8;
  }
  if (tradeMeAd && platform === 'both') {
    if (y + 40 > PAGE_H - 20) {
      doc.addPage();
      pageTracker.current++;
      addRunningHeader(doc, role);
      y = MARGIN_T + 16;
    }
    y = renderTradeMeAd(tradeMeAd, y);
  }
  if (tradeMeAd && platform === 'trademe') {
    y = renderTradeMeAd(tradeMeAd, y);
  }
};

// ─── SECTION 03 — APPLICANT SCREENING GRID ───────────────────────────────────
// Pre-interview twin of the scorecard: structured screening against fixed,
// job-relevant criteria set BEFORE any CV is opened, applied consistently to
// every applicant. Decision-support, not decision-making — the owner scores
// against their own confirmed criteria; nothing here reads or judges a person.
//
// DETERMINISTIC SPINE. The band structure, headers, scoring marks and guidance
// wording are hardcoded and identical on every kit. No model involvement in
// this file. Only the criterion strings flex.
//
// PLACEHOLDER CRITERIA — Kate-gated. The criterion strings below are generic
// scaffolding so the section renders and can be visually verified. They are NOT
// practitioner content. Replace post-Kate-review (Track B). Once the web flow
// passes confirmed criteria through, this function consumes those instead of
// the placeholders. Do not ship customer-facing with these placeholders live.
const SCREENING_PLACEHOLDER = {
  mustHaves: [
    'Relevant experience for the role', // PLACEHOLDER — Kate-gated
    'Right to work in NZ',              // PLACEHOLDER — Kate-gated
    'Any role-essential licence or certification', // PLACEHOLDER — Kate-gated
  ],
  niceToHaves: [
    'Experience in a similar business size or setting', // PLACEHOLDER — Kate-gated
    'Evidence of staying in roles (not short stints)',  // PLACEHOLDER — Kate-gated
    'A clear, specific application (not generic)',       // PLACEHOLDER — Kate-gated
  ],
  redFlags: [
    'Unexplained gaps with no account given',  // PLACEHOLDER — Kate-gated
    'Application does not address the role at all', // PLACEHOLDER — Kate-gated
  ],
};

const buildScreeningSection = (doc, role, screeningCriteria, pageTracker) => {
  const L = MARGIN_L;
  const W = CONTENT_W;
  const MAX_Y = PAGE_H - 20;

  const newPage = () => {
    doc.addPage();
    pageTracker.current++;
    addRunningHeader(doc, role);
    return MARGIN_T + 16;
  };

  doc.addPage();
  pageTracker.current++;
  addRunningHeader(doc, role);

  let y = addSectionHeader(doc, 3, 'Applicant Screening Grid',
    'Sift every CV against the same criteria before you decide who to interview.');

  // ── How to use this grid ─────────────────────────────────
  if (y + 40 > MAX_Y) y = newPage();

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(27, 79, 114);
  doc.text('How to use this grid', L, y);
  y += 6;

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(70, 70, 70);
  const howToLines = [
    '1. Set your criteria before you open a single CV. Deciding what matters up front is what keeps it fair and consistent.',
    '2. Score every applicant against the same grid. A must-have not met is usually a no. Nice-to-haves help you separate the maybes.',
    '3. A red flag is a prompt to look closer, not an automatic no — read it in context before you decide.',
    '4. Shortlist the applicants who clear your must-haves, then use the interview pack on them.',
  ];
  howToLines.forEach(line => {
    const wrapped = wrapText(doc, line, W);
    doc.text(wrapped, L, y);
    y += wrapped.length * 3.8;
  });
  y += 3;

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100, 100, 100);
  doc.text('Fixed criteria, applied the same way to everyone. That is what makes a fair sift.', L, y);
  y += 8;

  // Resolve criteria — confirmed criteria from the web flow when present,
  // otherwise the deterministic placeholder scaffold (Kate-gated).
  const crit = screeningCriteria && (
    (screeningCriteria.mustHaves && screeningCriteria.mustHaves.length) ||
    (screeningCriteria.niceToHaves && screeningCriteria.niceToHaves.length) ||
    (screeningCriteria.redFlags && screeningCriteria.redFlags.length)
  ) ? screeningCriteria : SCREENING_PLACEHOLDER;

  // ── One band (Must-haves / Nice-to-haves / Red flags) ────
  const drawBand = (bandTitle, bandHint, items, accentHex, markCols) => {
    if (!items || items.length === 0) return;

    // Band header strip
    if (y + 12 > MAX_Y) y = newPage();
    setFill(doc, accentHex);
    doc.rect(L, y, 1.5, 9, 'F');
    doc.setFillColor(235, 245, 251);
    doc.rect(L + 1.5, y, W - 1.5, 9, 'F');
    setFont(doc, 'bold', 8.5);
    setTextC(doc, NEAR_BLACK);
    doc.text(bandTitle, L + 4, y + 6);
    setFont(doc, 'italic', 7);
    setTextC(doc, MID_GREY);
    const hintW = doc.getTextWidth(bandHint);
    doc.text(bandHint, L + W - hintW - 3, y + 6);
    y += 11;

    // Column geometry: criterion text on the left, mark boxes on the right
    const marksW = markCols.length * 26;
    const labelW = W - marksW - 4;

    // Mark-column headers (small, above the first criterion only)
    setFont(doc, 'normal', 6.5);
    setTextC(doc, '#888888');
    markCols.forEach((m, mi) => {
      const mx = L + labelW + 4 + (mi * 26);
      doc.text(m, mx + 4, y + 1);
    });
    y += 3;

    items.forEach((item, idx) => {
      const itemText = typeof item === 'string'
        ? item
        : (item.label || item.text || item.criterion || '');
      const labelLines = wrapText(doc, itemText, labelW - 4);
      const rowH = Math.max(9, labelLines.length * 3.8 + 4);

      if (y + rowH > MAX_Y) {
        y = newPage();
        // Re-draw mark-column headers after a page break for orientation
        setFont(doc, 'normal', 6.5);
        setTextC(doc, '#888888');
        markCols.forEach((m, mi) => {
          const mx = L + labelW + 4 + (mi * 26);
          doc.text(m, mx + 4, y + 1);
        });
        y += 3;
      }

      // Row background (alternating)
      if (idx % 2 === 1) {
        setFill(doc, LIGHT_GREY);
        doc.rect(L, y, W, rowH, 'F');
      }

      // Criterion text
      setFont(doc, 'normal', 8.5);
      setTextC(doc, NEAR_BLACK);
      doc.text(labelLines, L + 2, y + 4.5);

      // Mark checkboxes
      markCols.forEach((m, mi) => {
        const bx = L + labelW + 4 + (mi * 26);
        doc.setDrawColor(150, 150, 150);
        doc.setLineWidth(0.3);
        doc.rect(bx, y + 2, 3, 3);
      });

      y += rowH;
    });

    y += 5;
  };

  // Must-haves: meets / partially / no
  drawBand(
    'Must-haves',
    'Essential — a miss here is usually a no',
    crit.mustHaves,
    '#1A7A6E',
    ['Meets', 'Partly', 'No']
  );

  // Nice-to-haves: yes / no
  drawBand(
    'Nice-to-haves',
    'Helps separate the maybes',
    crit.niceToHaves,
    '#B8860B',
    ['Yes', 'No']
  );

  // On-paper red flags: present? (look closer, not auto-reject)
  drawBand(
    'On-paper red flags',
    'A prompt to look closer, not an automatic no',
    crit.redFlags,
    '#C0392B',
    ['Present', 'No']
  );

  // ── Closing note ─────────────────────────────────────────
  if (y + 16 > MAX_Y) y = newPage();
  setFill(doc, '#F4F6F7');
  const closeNote = 'Add or change criteria to fit your role before you start — the grid only works if it reflects what actually matters for this hire. Screening narrows the field; the interview and scorecard make the call.';
  const closeLines = wrapText(doc, closeNote, W - 10);
  const closeH = closeLines.length * 4 + 10;
  doc.rect(L, y, W, closeH, 'F');
  setDraw(doc, '#E0E0E0');
  doc.setLineWidth(0.2);
  doc.rect(L, y, W, closeH);
  setFont(doc, 'italic', 8);
  setTextC(doc, MID_GREY);
  doc.text(closeLines, L + 5, y + 6);
};

// ─── CANDIDATE COMPARISON SHEET ──────────────────────────────────────────────
// Inserted after the last criterion table, before the Overall Recommendation block.
// Criteria labels are pulled dynamically from the same criteria array used in buildScorecardSection.
const buildCandidateComparisonSheet = (doc, role, criteria, pageTracker) => {
  const L = MARGIN_L;
  const W = CONTENT_W;
  const MAX_Y = PAGE_H - 20;

  doc.addPage();
  pageTracker.current++;
  addRunningHeader(doc, role);
  let y = MARGIN_T + 16;

  // Title
  setFont(doc, 'bold', 14);
  setTextC(doc, NEAR_BLACK);
  doc.text('Candidate Comparison Sheet', L, y);
  y += 7;

  // Subtitle
  setFont(doc, 'normal', 9);
  setTextC(doc, MID_GREY);
  doc.text("Fill this in after you've scored each candidate individually.", L, y);
  y += 10;

  // Table column geometry
  const labelColW = 52;
  const candColW  = (W - labelColW) / 3;
  const colXs = [
    L,
    L + labelColW,
    L + labelColW + candColW,
    L + labelColW + 2 * candColW,
  ];

  // Pull criteria labels dynamically (up to 5)
  const criteriaLabels = (criteria || []).slice(0, 5).map(c =>
    typeof c === 'string' ? c : (c.label || c.name || c.criterion || '')
  );

  // Helper: draw one table row
  const drawRow = (labelText, cells, bgHex, labelBold) => {
    const rowH = 9;
    if (y + rowH > MAX_Y) {
      doc.addPage();
      pageTracker.current++;
      addRunningHeader(doc, role);
      y = MARGIN_T + 16;
    }

    if (bgHex) {
      setFill(doc, bgHex);
      doc.rect(L, y, W, rowH, 'F');
    }

    // Outer border + internal vertical dividers
    setDraw(doc, '#CCCCCC');
    doc.setLineWidth(0.2);
    doc.rect(L, y, W, rowH);
    [labelColW, labelColW + candColW, labelColW + 2 * candColW].forEach(offset => {
      doc.line(L + offset, y, L + offset, y + rowH);
    });

    // Label text
    setFont(doc, labelBold ? 'bold' : 'normal', 7.5);
    setTextC(doc, NEAR_BLACK);
    const labelLines = wrapText(doc, labelText, labelColW - 4);
    doc.text(labelLines, L + 2, y + 5.5);

    // Cell content (centred in each candidate column)
    cells.forEach((content, ci) => {
      if (!content) return;
      setFont(doc, 'normal', 8);
      setTextC(doc, MID_GREY);
      doc.text(content, colXs[ci + 1] + candColW / 2, y + 5.5, { align: 'center' });
    });

    y += rowH;
  };

  // Column header row
  const hdrH = 8;
  setFill(doc, TEAL);
  doc.rect(L, y, W, hdrH, 'F');
  setDraw(doc, '#CCCCCC');
  doc.setLineWidth(0.2);
  doc.rect(L, y, W, hdrH);
  [labelColW, labelColW + candColW, labelColW + 2 * candColW].forEach(offset => {
    doc.line(L + offset, y, L + offset, y + hdrH);
  });
  ['Candidate A', 'Candidate B', 'Candidate C'].forEach((hdr, ci) => {
    setFont(doc, 'bold', 8);
    setTextC(doc, WHITE);
    doc.text(hdr, colXs[ci + 1] + candColW / 2, y + 5, { align: 'center' });
  });
  y += hdrH;

  // Row 1 — Gates (visually distinct: light blue, pass/fail — NOT scored)
  drawRow('Gates passed? (Y / N)', ['', '', ''], '#D6EAF8', true);

  // Separator line to reinforce gates = pass/fail, not scored
  setDraw(doc, TEAL);
  doc.setLineWidth(0.4);
  doc.line(L, y, L + W, y);

  // Rows 2–6 — Role criteria (dynamic)
  criteriaLabels.forEach((label, idx) => {
    drawRow(label, ['_ / 4', '_ / 4', '_ / 4'], idx % 2 === 0 ? WHITE : LIGHT_GREY, false);
  });

  // Row 7 — Totals (shaded grey, bold)
  drawRow('TOTAL (out of 20)', ['', '', ''], '#EBEBEB', true);

  // Row 8 — Stop-and-look (amber warning background)
  drawRow("Any single 'No' (a 1)?", ['', '', ''], '#FFF3CD', false);

  y += 5;

  // Notes below the table (not inside it)
  setFont(doc, 'normal', 7.5);
  setTextC(doc, '#C0392B');
  doc.text(
    'Any gate failed = candidate is out. Don\u2019t proceed to scoring.',
    L, y
  );
  y += 5;
  setFont(doc, 'italic', 7.5);
  setTextC(doc, '#856404');
  const noNote = wrapText(doc,
    'A No isn\u2019t cancelled out by a high total \u2014 stop and decide deliberately whether it\u2019s a dealbreaker for this role.',
    W
  );
  doc.text(noNote, L, y);
  y += noNote.length * 4 + 8;

  // ── Decision rule block ──────────────────────────────────────
  const decisionSteps = [
    'Any gate failed? That candidate is out. Stop \u2014 don\u2019t score them further.',
    'Any single \u2018No\u2019 (a 1) on a criterion? Stop and look before you go further. A No isn\u2019t cancelled out by a high total \u2014 decide deliberately whether it\u2019s a dealbreaker for this role.',
    'Otherwise, highest total is your lead candidate.',
    'Scores within a point or two of each other? Treat them as a tie \u2014 go back to the one thing that matters most for this role and compare just that.',
  ];
  const closingText = 'This gives you a clear, consistent way to compare. The final call is still yours.';

  // Pre-calculate block height so we can draw background before text
  setFont(doc, 'normal', 9);
  const stepWraps = decisionSteps.map(s => wrapText(doc, s, W - 16));
  setFont(doc, 'normal', 8);
  const closingWraps = wrapText(doc, closingText, W - 10);
  let blockH = 14; // title + top padding
  stepWraps.forEach(sw => { blockH += sw.length * 4.5 + 4; });
  blockH += closingWraps.length * 4 + 10;

  if (y + blockH > MAX_Y) {
    doc.addPage();
    pageTracker.current++;
    addRunningHeader(doc, role);
    y = MARGIN_T + 16;
  }

  const blockX = L;
  const blockStartY = y;

  // Draw light background for the block
  setFill(doc, '#F4F6F7');
  doc.rect(blockX, blockStartY, W, blockH, 'F');
  setDraw(doc, '#E0E0E0');
  doc.setLineWidth(0.2);
  doc.rect(blockX, blockStartY, W, blockH);

  const pad = 5;
  y = blockStartY + pad + 5;

  setFont(doc, 'bold', 10);
  setTextC(doc, NEAR_BLACK);
  doc.text('How to decide', blockX + pad, y);
  y += 8;

  decisionSteps.forEach((step, si) => {
    setFont(doc, 'bold', 9);
    setTextC(doc, NEAR_BLACK);
    doc.text(`${si + 1}.`, blockX + pad, y);
    setFont(doc, 'normal', 9);
    setTextC(doc, NEAR_BLACK);
    doc.text(stepWraps[si], blockX + pad + 7, y);
    y += stepWraps[si].length * 4.5 + 4;
  });

  y += 2;
  setFont(doc, 'italic', 8);
  setTextC(doc, MID_GREY);
  doc.text(closingWraps, blockX + pad, y);
};

// ─── SECTION 05 — SCORECARD ───────────────────────────────────────────────────
const buildScorecardSection = (doc, role, criteria, pageTracker) => {
  const L = MARGIN_L;
  const W = CONTENT_W;
  const MAX_Y = PAGE_H - 20;

  const newPage = () => {
    doc.addPage();
    pageTracker.current++;
    addRunningHeader(doc, role);
    return MARGIN_T + 16;
  };

  doc.addPage();
  pageTracker.current++;
  addRunningHeader(doc, role);

  let y = addSectionHeader(doc, 5, 'Candidate Scorecard',
    'Rate each candidate against these criteria. Compliance gates are pass/fail — any fail is a no-hire.');

  // ── Candidate header block ───────────────────────────────
  doc.setFillColor(242, 243, 244);
  doc.rect(L, y, W, 14, 'F');
  doc.setFontSize(7.5);
  doc.setTextColor(100, 100, 100);
  doc.text('Interviewer:', L + 2, y + 5);
  doc.text('______________________', L + 22, y + 5);
  doc.text('Candidate:', L + 82, y + 5);
  doc.text('______________________', L + 100, y + 5);
  doc.text('Role:', L + 2, y + 11);
  doc.setTextColor(27, 79, 114);
  doc.text(role || 'Not specified', L + 14, y + 11);
  doc.setTextColor(100, 100, 100);
  doc.text('Date:', L + 82, y + 11);
  doc.text('______________________', L + 94, y + 11);
  y += 18;

  // ── How to use this scorecard ────────────────────────────
  if (y + 45 > MAX_Y) y = newPage();

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(27, 79, 114);
  doc.text('How to use this scorecard', L, y);
  y += 6;

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(70, 70, 70);
  const howToLines = [
    '1. Score during or right after each interview — not at the end of the day. You\'ll forget the detail faster than you think.',
    '2. For each criterion, read the four descriptions and pick the one that sounds most like what you actually heard. Don\'t overthink it — go with your read of the evidence.',
    '3. After you\'ve interviewed everyone, fill in the comparison sheet at the end. Highest total is usually your lead candidate.',
    '4. A \'No\' on any single criterion is a flag — stop and decide if it\'s a dealbreaker for this role, even if the total looks good.',
  ];
  howToLines.forEach(line => {
    const wrapped = wrapText(doc, line, CONTENT_W);
    doc.text(wrapped, L, y);
    y += wrapped.length * 3.8;
  });
  y += 2;

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100, 100, 100);
  doc.text('The structure does the work. You just score what you heard.', L, y);
  y += 6;

  y += 4;

  // ── Compliance gates strip ───────────────────────────────
  const gates = criteria?.[0]?.complianceGates || null;

  if (gates && gates.length > 0) {
    if (y + 10 > MAX_Y) y = newPage();

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(27, 79, 114);
    doc.text('Compliance Gates — verify before scoring', L, y + 4);
    y += 8;

    const hasHard = gates.some(g => g.level === 'hard');
    if (hasHard) {
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(192, 57, 43);
      doc.text('Any hard gate fail = no-hire. Do not proceed to scoring.', L, y + 3);
      y += 7;
    }

    gates.forEach(gate => {
      if (y + 14 > MAX_Y) y = newPage();
      const isHard = gate.level === 'hard';

      // Left border
      doc.setFillColor(isHard ? 192 : 230, isHard ? 57 : 126, isHard ? 43 : 34);
      doc.rect(L, y, 1.5, 12, 'F');

      // Background
      doc.setFillColor(isHard ? 253 : 253, isHard ? 237 : 243, isHard ? 236 : 230);
      doc.rect(L + 1.5, y, W - 1.5, 12, 'F');

      // Gate label
      doc.setFontSize(7.5);
      doc.setFont('helvetica', isHard ? 'bold' : 'normal');
      doc.setTextColor(50, 50, 50);
      doc.text(gate.label, L + 4, y + 4.5);

      // Checkboxes
      const boxY = y + 7.5;
      (gate.level === 'conditional'
        ? ['Verified', 'Not yet', 'Fail', 'N/A']
        : ['Verified', 'Not yet', 'Fail']
      ).forEach((lbl, ci) => {
        const bx = L + 4 + (ci * 30);
        doc.setDrawColor(150, 150, 150);
        doc.rect(bx, boxY - 2.5, 3, 3);
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80, 80, 80);
        doc.text(lbl, bx + 4, boxY);
      });

      y += 14;
    });
    y += 4;
  }

  // ── Scored criteria ──────────────────────────────────────
  (criteria || []).forEach((criterion) => {
    const label = typeof criterion === 'string'
      ? criterion
      : (criterion.label || criterion.name || criterion.criterion || '');
    const anchors = criterion?.anchors || null;

    // Estimate height: label row + optional 2×2 grid + rating row + notes
    const estH = anchors ? 10 + 52 + 10 + 18 + 4 : 26;
    if (y + estH > MAX_Y) y = newPage();

    // D1 — Criterion label row
    doc.setFillColor(235, 245, 251);
    doc.rect(L, y, W, 9, 'F');
    doc.setFillColor(27, 79, 114);
    doc.rect(L, y, 1.5, 9, 'F');
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(27, 79, 114);
    doc.text(label, L + 4, y + 6);
    y += 10;

    // D2 — Anchor grid (2×2) when anchors available
    if (anchors) {
      const cellW = W / 2;
      const cols = [
        { key: 'no',        label: 'No',          num: '1', text: [192,57,43],  fill: [250,219,216] },
        { key: 'maybe',     label: 'Maybe',        num: '2', text: [230,126,34], fill: [253,235,208] },
        { key: 'yes',       label: 'Yes',          num: '3', text: [26,122,74],  fill: [213,245,227] },
        { key: 'strongYes', label: 'Strong yes',   num: '4', text: [20,90,140],  fill: [214,234,248] },
      ];
      const cellH = 36;

      [[0,1],[2,3]].forEach(([ci1, ci2]) => {
        if (y + cellH > MAX_Y) y = newPage();
        [cols[ci1], cols[ci2]].forEach((col, colIdx) => {
          const cx = L + (colIdx * cellW);
          const bullets = anchors[col.key] || [];
          doc.setFillColor(...col.fill);
          doc.rect(cx, y, cellW, cellH, 'F');
          doc.setDrawColor(189, 195, 199);
          doc.setLineWidth(0.3);
          doc.rect(cx, y, cellW, cellH);
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...col.text);
          doc.text(col.label, cx + 2, y + 5);
          // Numeric annotation: secondary smaller muted label alongside the word
          const labelW = doc.getTextWidth(col.label);
          doc.setFontSize(6);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(160, 160, 160);
          doc.text(`(${col.num})`, cx + 2 + labelW + 1, y + 5);
          doc.setFontSize(6.8);
          doc.setFont('helvetica', 'normal');
          let bulletY = y + 9;
          bullets.forEach((bullet) => {
            const by = bulletY;
            doc.setTextColor(...col.text);
            doc.text('•', cx + 2, by);
            doc.setTextColor(40, 40, 40);
            const wrapped = doc.splitTextToSize(bullet, cellW - 7);
            // Render all wrapped lines, not just the first
            wrapped.forEach((line, li) => {
              doc.text(line, cx + 5, by + (li * 3.2));
            });
            bulletY += 3.2 + ((wrapped.length - 1) * 3.2);
          });
        });
        y += cellH;
      });
    }

    // D3 — Rating selector row (No / Maybe / Yes / Strong yes)
    y += 2;
    if (y + 8 > MAX_Y) y = newPage();
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text('Rating:', L, y + 3);
    [
      { label: 'No',         num: '1' },
      { label: 'Maybe',      num: '2' },
      { label: 'Yes',        num: '3' },
      { label: 'Strong yes', num: '4' },
    ].forEach((opt, ri) => {
      const rx = L + 14 + (ri * 30);
      doc.setDrawColor(150, 150, 150);
      doc.rect(rx, y + 0.5, 3, 3);
      doc.setFontSize(6.8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text(opt.label, rx + 4, y + 3.5);
      // Numeric annotation: secondary smaller muted label alongside the word
      const optLabelW = doc.getTextWidth(opt.label);
      doc.setFontSize(6);
      doc.setTextColor(150, 150, 150);
      doc.text(`(${opt.num})`, rx + 4 + optLabelW + 1.5, y + 3.5); // FIX: x-offset adjusted to produce space before (num) — Sprint 17
    });
    y += 8;

    // D4 — Notes lines
    if (y + 14 > MAX_Y) y = newPage();
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text('Notes:', L, y + 3);
    doc.setDrawColor(189, 195, 199);
    doc.setLineWidth(0.3);
    [0, 5, 10].forEach(offset => {
      doc.line(L + 12, y + 3 + offset, L + W, y + 3 + offset);
    });
    y += 16;
    y += 4;
  });

  // ── Candidate Comparison Sheet ───────────────────────────
  buildCandidateComparisonSheet(doc, role, criteria, pageTracker);
};

// ─── WARM-UP QUESTIONS — static, Kate-authored, not scored ───────────────────
export const WARMUP_QUESTIONS = [
  {
    id: 'W1',
    text: (role, company) =>
      `Before we get into the detail — how's your day been? Did you find us alright?`,
  },
  {
    id: 'W2',
    text: (role, company) =>
      company
        ? `Tell me a bit about where you're at right now — what you're doing day to day, and what made you put your hand up for the ${role} role here at ${company}.`
        : `Tell me a bit about where you're at right now — what you're doing day to day, and what made you put your hand up for this ${role} role.`,
  },
];

// ─── SECTION 04 — INTERVIEW PACK ─────────────────────────────────────────────
const buildInterviewSection = (doc, role, businessName, interviewPack, pageTracker) => {
  doc.addPage();
  pageTracker.current++;
  addRunningHeader(doc, role);

  // Question count derived from actual data — not hardcoded
  const questionCount = (interviewPack?.questions?.length) || 0;
  const sectionSubtitle = questionCount > 0
    ? `${questionCount} tailored questions with coaching notes`
    : 'Tailored questions with coaching notes';
  let y = addSectionHeader(doc, 4, 'Interview Pack', sectionSubtitle);

  const pack = interviewPack;
  if (!pack) {
    setFont(doc, 'normal', 10);
    setTextC(doc, MID_GREY);
    doc.text('Interview pack not available for this role.', MARGIN_L, y);
    return;
  }

  // ── Warm-up segment ──────────────────────────────────────────────────────
  const warmupCompany = businessName || '';

  setFont(doc, 'italic', 8);
  setTextC(doc, '#888888');
  doc.text('Warm-up — not scored  (~5 min)', MARGIN_L, y);
  y += 5;

  setFont(doc, 'normal', 7.5);
  setTextC(doc, '#555555');
  const wuInstrLines = wrapText(doc, 'Two easy questions to settle the candidate in. Do not score these — note what you hear, but assess from Q1 onward.', CONTENT_W);
  doc.text(wuInstrLines, MARGIN_L, y);
  y += wuInstrLines.length * 4.5 + 5;

  WARMUP_QUESTIONS.forEach((wq) => {
    if (y + 22 > PAGE_H - 20) {
      doc.addPage();
      pageTracker.current++;
      addRunningHeader(doc, role);
      y = MARGIN_T + 16;
    }
    setFont(doc, 'bold', 10);
    setTextC(doc, TEAL);
    doc.text(wq.id, MARGIN_L, y + 4);
    y += 8;
    setFont(doc, 'bold', 12);
    setTextC(doc, NEAR_BLACK);
    const wqLines = wrapText(doc, wq.text(role, warmupCompany), CONTENT_W - 10);
    doc.text(wqLines, MARGIN_L, y);
    y += wqLines.length * 5.5 + 8;
  });

  // Separator before characterPressure / questions
  setDraw(doc, '#E0E0E0');
  doc.setLineWidth(0.3);
  doc.line(MARGIN_L, y, MARGIN_L + CONTENT_W, y);
  y += 8;

  if (pack.openingNote) {
    setFont(doc, 'normal', 10);
    setTextC(doc, MID_GREY);
    const noteLines = wrapText(doc, pack.openingNote, CONTENT_W);
    doc.text(noteLines, MARGIN_L, y);
    y += noteLines.length * 5 + 6;
  }

  if (pack.characterPressure) {
    setFill(doc, NEAR_BLACK);
    doc.rect(MARGIN_L, y, CONTENT_W, 18, 'F');
    setFont(doc, 'bold', 7);
    setTextC(doc, '#888888');
    doc.text('CHARACTER PRESSURE FOR THIS HIRE', MARGIN_L + 4, y + 6);
    setFont(doc, 'normal', 10);
    setTextC(doc, WHITE);
    const cpLines = wrapText(doc, `"${pack.characterPressure}"`, CONTENT_W - 8);
    doc.text(cpLines, MARGIN_L + 4, y + 12);
    y += 22;
  }

  // Iterates all questions with no slice() limit
  (pack.questions || []).forEach((q, i) => {
    const qNum = q.number || i + 1;

    // Compute wraps with correct font sizes for accurate height estimates
    setFont(doc, 'bold', 12);
    const questionLines = wrapText(doc, q.question, CONTENT_W - 10);
    setFont(doc, 'normal', 10);
    const strongLines   = wrapText(doc, q.strongAnswer || '', CONTENT_W - 14);
    const watchLines    = wrapText(doc, q.watchFor || '', CONTENT_W - 14);
    const weakLines     = wrapText(doc, q.weakAnswer || '', CONTENT_W - 14);
    const estH = 16 + (questionLines.length * 5.5) + (strongLines.length * 5) + (watchLines.length * 5) + (weakLines.length * 5) + 12;

    // Break before any question that won't fit — including Q1.
    if (y + estH > PAGE_H - 20) {
      doc.addPage();
      pageTracker.current++;
      addRunningHeader(doc, role);
      y = MARGIN_T + 16;
    }

    // Q number + category: 10pt bold teal caps with letter spacing
    setFont(doc, 'bold', 10);
    setTextC(doc, TEAL);
    if (typeof doc.setCharSpace === 'function') doc.setCharSpace(0.6);
    const qLabel = q.category ? `Q${qNum}  ${q.category.toUpperCase()}` : `Q${qNum}`;
    doc.text(qLabel, MARGIN_L, y + 4);
    if (typeof doc.setCharSpace === 'function') doc.setCharSpace(0);
    y += 8;

    // Question text: 12pt bold black — reading anchor
    setFont(doc, 'bold', 12);
    setTextC(doc, NEAR_BLACK);
    doc.text(questionLines, MARGIN_L, y);
    y += questionLines.length * 5.5 + 4;

        if (q.strongAnswer) {
      const saH = strongLines.length * 5 + 14;
      if (y + saH > PAGE_H - 20) {
        doc.addPage(); pageTracker.current++; addRunningHeader(doc, role); y = MARGIN_T + 16;
      }
      setFill(doc, TEAL);
      doc.rect(MARGIN_L, y, 2.5, saH, 'F');
      setFont(doc, 'bold', 8);
      setTextC(doc, MID_GREY);
      if (typeof doc.setCharSpace === 'function') doc.setCharSpace(0.4);
      drawCheckmark(doc, MARGIN_L + 5, y + 4.5, 5);
      doc.text('WHAT A STRONG ANSWER SOUNDS LIKE', MARGIN_L + 12, y + 6);
      if (typeof doc.setCharSpace === 'function') doc.setCharSpace(0);
      setFont(doc, 'normal', 10);
      setTextC(doc, MID_GREY);
      doc.text(strongLines, MARGIN_L + 5, y + 12);
      y += saH + 3;
    }

        if (q.watchFor) {
      const wfH = watchLines.length * 5 + 14;
      if (y + wfH > PAGE_H - 20) {
        doc.addPage(); pageTracker.current++; addRunningHeader(doc, role); y = MARGIN_T + 16;
      }
      const [ar,ag,ab] = hex2rgb('#B8860B');
      doc.setFillColor(ar,ag,ab);
      doc.rect(MARGIN_L, y, 2.5, wfH, 'F');
      setFont(doc, 'bold', 8);
      setTextC(doc, MID_GREY);
      if (typeof doc.setCharSpace === 'function') doc.setCharSpace(0.4);
      doc.text('WATCH FOR THE REHEARSED ANSWER', MARGIN_L + 5, y + 6);
      if (typeof doc.setCharSpace === 'function') doc.setCharSpace(0);
      setFont(doc, 'normal', 10);
      setTextC(doc, MID_GREY);
      doc.text(watchLines, MARGIN_L + 5, y + 12);
      y += wfH + 3;
    }

        if (q.weakAnswer) {
      const waH = weakLines.length * 5 + 14;
      if (y + waH > PAGE_H - 20) {
        doc.addPage(); pageTracker.current++; addRunningHeader(doc, role); y = MARGIN_T + 16;
      }
      const [wr,wg,wb] = hex2rgb('#CC4444');
      doc.setFillColor(wr,wg,wb);
      doc.rect(MARGIN_L, y, 2.5, waH, 'F');
      setFont(doc, 'bold', 8);
      setTextC(doc, MID_GREY);
      if (typeof doc.setCharSpace === 'function') doc.setCharSpace(0.4);
      doc.text('WHAT A WEAK ANSWER LOOKS LIKE', MARGIN_L + 5, y + 6);
      if (typeof doc.setCharSpace === 'function') doc.setCharSpace(0);
      setFont(doc, 'normal', 10);
      setTextC(doc, MID_GREY);
      doc.text(weakLines, MARGIN_L + 5, y + 12);
      y += waH + 3;
    }

    setDraw(doc, '#EEEEEE');
    doc.setLineWidth(0.2);
    doc.line(MARGIN_L, y + 3, PAGE_W - MARGIN_R, y + 3);
    y += 10;
  });

  if (pack.complianceNote) {
    if (y + 24 > PAGE_H - 20) {
      doc.addPage();
      pageTracker.current++;
      addRunningHeader(doc, role);
      y = MARGIN_T + 16;
    }
    setFill(doc, LIGHT_GREY);
    const cnLines = wrapText(doc, pack.complianceNote, CONTENT_W - 10);
    const cnH = cnLines.length * 4.5 + 14;
    doc.rect(MARGIN_L, y, CONTENT_W, cnH, 'F');
    setFont(doc, 'bold', 8);
    setTextC(doc, NEAR_BLACK);
    doc.text('NZ COMPLIANCE NOTE', MARGIN_L + 4, y + 7);
    setFont(doc, 'normal', 9);
    setTextC(doc, MID_GREY);
    doc.text(cnLines, MARGIN_L + 4, y + 13);
    y += cnH + 6;
  }

  if (pack.creditLine) {
    setFont(doc, 'normal', 7);
    setTextC(doc, '#999999');
    doc.text(pack.creditLine, MARGIN_L, y);
  }
};

// ─── SECTION 02 — SALARY BENCHMARK ───────────────────────────────────────────
const buildSalarySection = (doc, role, location, salaryData, pageTracker) => {
  doc.addPage();
  pageTracker.current++;
  addRunningHeader(doc, role);

  let y = addSectionHeader(doc, 2, 'NZ Pay Benchmark', 'A pay reference point for your role and region');

  const formatVal = (val) => {
    if (!val) return 'Not available';
        if (typeof val === 'string' && val.startsWith('$')) return val.replace(/\/hr$/i, '');
    const num = parseFloat(String(val).replace(/[^0-9.]/g, ''));
        // Unit is stated by the "per hour" / "per year" label line below — don't repeat it here.
    return isNaN(num) ? 'Not available' : num >= 1000 ? `$${num.toLocaleString()}` : `$${num}`;
  };
  const formatRange = (val) => {
    if (!val) return 'Not available';
    if (typeof val === 'string') return val;
    return 'Not available';
  };

  if (salaryData && salaryData.found) {
    // Thin teal rule anchors the data block to the top of the content area
    setDraw(doc, TEAL);
    doc.setLineWidth(0.5);
    doc.line(MARGIN_L, y, PAGE_W - MARGIN_R, y);
    y += 10;

    // Median: 32pt bold teal — visual anchor of the page
    const isHourly = salaryData.median && String(salaryData.median).toLowerCase().includes('hr');
    setFont(doc, 'bold', 32);
    setTextC(doc, TEAL);
    doc.text(formatVal(salaryData.median), MARGIN_L, y);
    y += 11;

    // "per year" / "per hour" label: 11pt grey
    setFont(doc, 'normal', 11);
    setTextC(doc, MID_GREY);
    doc.text(isHourly ? 'per hour' : 'per year', MARGIN_L, y);
    y += 9;

    // Range: 13pt bold black
    setFont(doc, 'bold', 13);
    setTextC(doc, NEAR_BLACK);
    doc.text(`Range: ${formatRange(salaryData.range)}`, MARGIN_L, y);
    y += 8;

    // Confidence: 10pt grey
    setFont(doc, 'normal', 10);
    setTextC(doc, MID_GREY);
    doc.text(`Confidence: ${salaryData.confidence || ''}`, MARGIN_L, y);
    y += 10;

    if (salaryData.note) {
      setFont(doc, 'normal', 8);
      setTextC(doc, '#777777');
      const noteLines = wrapText(doc, salaryData.note, CONTENT_W);
      doc.text(noteLines, MARGIN_L, y);
      y += noteLines.length * 4.5 + 4;
    }

    // Source attribution: 9pt grey italic
    setFont(doc, 'italic', 9);
    setTextC(doc, '#999999');
    doc.text(`${salaryData.source || 'RoleReady NZ Pay Ranges · April 2026 review'}`, MARGIN_L, y);
    y += 12;

    // Methodology note
    setFont(doc, 'normal', 8);
    setTextC(doc, '#777777');
    const methodNote = `Why this number: Derived from current NZ advertised salary ranges, aggregated and reviewed June 2026. The median is the midpoint of current advertised roles — roughly half of comparable positions are above this rate, half below. The range reflects typical variation by experience and business size. Use this as a market reference when setting your offer, not as a precise figure.`;
    const methodLines = wrapText(doc, methodNote, CONTENT_W);
    doc.text(methodLines, MARGIN_L, y);
    y += methodLines.length * 4.5 + 4;
  } else {
    y += 8;
    setFont(doc, 'normal', 10);
    setTextC(doc, MID_GREY);
    const missNote = `We don't yet have verified market data for this role in ${location || 'your region'}. When you're setting pay, it's worth checking what similar ${role} roles are advertising on SEEK and Trade Me right now. That's where your candidates are looking too.`;
    const missLines = wrapText(doc, missNote, CONTENT_W);
    doc.text(missLines, MARGIN_L, y);
  }
};

// ─── NEXT STEPS ───────────────────────────────────────────────────────────────
const buildNextSteps = (doc, role, pageTracker) => {
  doc.addPage();
  pageTracker.current++;
  addRunningHeader(doc, role);

  let y = MARGIN_T + 16;

  // Headline: 18pt bold
  setFont(doc, 'bold', 18);
  setTextC(doc, NEAR_BLACK);
  doc.text("Sweet — you're sorted.", MARGIN_L, y);
  y += 18;

  // Five numbered steps — tighter vertical spacing as a cohesive block
  [
    { n: '1', title: 'Post your job ad',          body: 'Copy the formatted ad from Section 01 directly into SEEK NZ or Trade Me Jobs.' },
    { n: '2', title: 'Make your offer',            body: 'Use Section 02 as your pay reference when you set the offer.' },
    { n: '3', title: 'Screen your applicants',     body: 'Set your criteria in Section 03 and sift every CV against them before you shortlist.' },
    { n: '4', title: 'Follow the interview pack',  body: 'Work through Section 04 question by question. The coaching notes tell you what good looks like.' },
    { n: '5', title: 'Use the scorecard',          body: 'Print Section 05 and use it for every candidate interview. Same criteria, every time.' },
  ].forEach(step => {
    const bodyLines = wrapText(doc, step.body, CONTENT_W - 14);
    const blockH = bodyLines.length * 4.5 + 16;
    setFill(doc, LIGHT_GREY);
    doc.rect(MARGIN_L, y, CONTENT_W, blockH, 'F');
    setFill(doc, TEAL);
    doc.rect(MARGIN_L, y, 8, blockH, 'F');
    setFont(doc, 'bold', 9);
    setTextC(doc, WHITE);
    doc.text(step.n, MARGIN_L + 2.5, y + blockH/2 + 2);
    setFont(doc, 'bold', 9);
    setTextC(doc, NEAR_BLACK);
    doc.text(step.title, MARGIN_L + 12, y + 7);
    setFont(doc, 'normal', 9);
    setTextC(doc, MID_GREY);
    doc.text(bodyLines, MARGIN_L + 12, y + 12);
    y += blockH + 2;
  });

  // Compliance note between steps and CTA
  y += 6;
  setFont(doc, 'normal', 9);
  setTextC(doc, MID_GREY);
  const compLines = wrapText(doc, "Doesn't replace legal advice for your specific situation.", CONTENT_W);
  doc.text(compLines, MARGIN_L, y);
  y += compLines.length * 4.5 + 16;

  // Closing CTA: 11pt teal, centred
  setFont(doc, 'normal', 11);
  setTextC(doc, TEAL);
  doc.text("When you're ready to hire again, your next kit takes the same 15 minutes.", PAGE_W / 2, y, { align: 'center' });

  // Thin teal rule above footer area — deliberate visual close
  setDraw(doc, TEAL);
  doc.setLineWidth(0.5);
  doc.line(MARGIN_L, PAGE_H - 16, PAGE_W - MARGIN_R, PAGE_H - 16);
};

// ─── SHARED BUILDER ──────────────────────────────────────────────────────────
// Builds the complete jsPDF doc and returns { doc, filename }. Both the browser
// download (generateHiringKitPDF) and the email path (getHiringKitPDFBase64)
// call this, so the emailed PDF is byte-identical to the downloaded one.
const buildHiringKitDoc = ({ role, location, businessName, jobAd, jobAdData, platform, scorecardCriteria, apiScorecardCriteria, screeningCriteria, interviewPack, salaryData }) => {
  // jobAd and scorecardCriteria kept as params for backwards compat — not used directly
  void jobAd;

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const generatedDate = new Date().toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' });
  const pageTracker   = { current: 1 };

  // ── Pass 1: Build all content (no footers yet) ────────────────────────────
  buildCoverPage(doc, role, location, businessName || '', generatedDate);

  pageTracker.current = 2;
  buildJobAdSection(doc, role, location, jobAdData, platform || 'seek', pageTracker);
  buildSalarySection(doc, role, location, salaryData, pageTracker);
  // Section 03 — Applicant Screening Grid. screeningCriteria is undefined until
  // the web flow passes confirmed criteria; the section falls back to the
  // deterministic Kate-gated placeholder scaffold in the meantime.
  buildScreeningSection(doc, role, screeningCriteria ?? null, pageTracker);
  buildInterviewSection(doc, role, businessName || '', interviewPack, pageTracker);
  const enrichedCriteria = getEnrichedCriteria(role, apiScorecardCriteria ?? scorecardCriteria ?? null);
  buildScorecardSection(doc, role, enrichedCriteria, pageTracker);
  buildNextSteps(doc, role, pageTracker);

  // ── Pass 2: Render footers with correct total ────────────────────────────
  // Fix 1: totalPages only known after all pages are built
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    renderFooter(doc, i, totalPages);
  }

  const filename = `RoleReady-${(role || 'HiringKit').replace(/\s+/g,'-')}-${(location || 'NZ').replace(/\s+/g,'-')}.pdf`;
  return { doc, filename };
};

// ─── MAIN EXPORT — browser download (unchanged behaviour) ────────────────────
export const generateHiringKitPDF = (params) => {
  const { doc, filename } = buildHiringKitDoc(params);
  doc.save(filename);
};

// ─── EMAIL EXPORT — same PDF, returned as base64 for server attachment ───────
// Returns { base64, filename }. base64 is the raw PDF content WITHOUT any data:
// URI prefix, ready to hand to Resend's attachments[].content field.
export const getHiringKitPDFBase64 = (params) => {
  const { doc, filename } = buildHiringKitDoc(params);
  // 'datauristring' → "data:application/pdf;filename=...;base64,XXXX". Strip to raw base64.
  const datauri = doc.output('datauristring');
  const base64 = datauri.substring(datauri.indexOf('base64,') + 'base64,'.length);
  return { base64, filename };
};

